import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { formatNumber } from '@/utils/format'
import { OVERLAY_MESSAGE_TYPE, type OverlayMessage } from '@/utils/politicalPreview'

interface OverlayMetrics {
  width: number
  height: number
  coverage: number
}

export interface PoliticalPreviewFrameProps {
  /** Rendered preview HTML, already prepared by `preparePoliticalPreviewHtml`. */
  html: string
  width: number
  height: number
  /** Full-screen fills the column instead of keeping the creative's size. */
  fullScreen: boolean
  /** Describes where the image came from, shown with the measurements. */
  imageSourceLabel: string
}

/**
 * A political creative in a sandboxed frame, with the size of its TTPA overlay
 * and how much of the creative it covers. The frame reports the overlay size
 * itself, as the sandbox keeps the page from reading the frame's document.
 * Mount it with a fresh `key` per render so the measurements start over.
 */
export function PoliticalPreviewFrame({
  html,
  width,
  height,
  fullScreen,
  imageSourceLabel,
}: PoliticalPreviewFrameProps) {
  const { t } = useTranslation()
  const frame = useRef<HTMLIFrameElement>(null)
  const [metrics, setMetrics] = useState<OverlayMetrics | null>(null)
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== frame.current?.contentWindow) return
      const data = event.data as OverlayMessage
      if (data?.type !== OVERLAY_MESSAGE_TYPE) return
      if (data.unavailable || !data.width || !data.height) {
        setUnavailable(true)
        return
      }
      const frameArea = (data.frameWidth || width) * (data.frameHeight || height)
      setMetrics({
        width: data.width,
        height: data.height,
        coverage: frameArea ? Math.round(((data.width * data.height) / frameArea) * 100) : 0,
      })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [width, height])

  const pending = !metrics && !unavailable
  const measurement = (value: string) =>
    metrics
      ? value
      : pending
        ? t('politicalPreview.measuring')
        : t('politicalPreview.measureUnavailable')

  return (
    <div className="flex flex-col gap-4">
      <div className={cn('max-w-full', fullScreen ? 'overflow-hidden' : 'overflow-auto')}>
        <iframe
          ref={frame}
          title={t('politicalPreview.preview')}
          srcDoc={html}
          scrolling="no"
          sandbox="allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox"
          className={cn('block border-0', fullScreen && 'h-[70vh] min-h-120 w-full')}
          width={fullScreen ? undefined : width}
          height={fullScreen ? undefined : height}
        />
      </div>

      <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="font-semibold">{t('politicalPreview.previewDimensions')}</dt>
          <dd className="text-(--ad-text-muted)">
            {fullScreen ? t('politicalPreview.layoutFullScreen') : `${width} × ${height}`}
          </dd>
        </div>
        <div>
          <dt className="font-semibold">{t('politicalPreview.overlayDimensions')}</dt>
          <dd className="text-(--ad-text-muted)">
            {measurement(metrics ? `${metrics.width} × ${metrics.height}` : '')}
          </dd>
        </div>
        <div>
          <dt className="font-semibold">{t('politicalPreview.imageSource')}</dt>
          <dd className="break-words text-(--ad-text-muted)">{imageSourceLabel}</dd>
        </div>
        <div>
          <dt className="font-semibold">{t('politicalPreview.overlayCoverage')}</dt>
          <dd className="text-(--ad-text-muted)">
            {measurement(metrics ? `${formatNumber(metrics.coverage)}%` : '')}
          </dd>
        </div>
      </dl>
    </div>
  )
}
